import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Step as MuiStep, StepContent as MuiStepContent, StepLabel as MuiStepLabel, } from '@mui/material';
import { dissoc } from 'ramda';
import { Fragment, useCallback, useMemo, useRef } from 'react';
import { useMediaQuery } from '../../../hooks';
import { styled } from '../../utils';
import { Skeleton } from '../Skeleton';
import { Typography } from '../Typography';
import { StepIconHorizontal, StepIconVertical } from './StepIcon';
import { StepNavigation } from './StepNavigation';
import { breakpointFallback } from './Stepper';
export function Step(props) {
    const matches = useMediaQuery((theme) => theme.breakpoints.up(props.breakpoint ?? breakpointFallback));
    const StepComponent = !matches || props.orientation === 'horizontal'
        ? StepHorizontal
        : StepVertical;
    return _jsx(StepComponent, { ...props, active: props.activeStep === props.index });
}
// StepHorizontal
function StepHorizontal({ loading, ...props }) {
    const { first, last, disabledNext, disabledPrev, onClickPrev, onClickNext, onClickTitle, children, label, subtitle, labelNext, labelPrev, scrollOffset, forcedStep, setAnimating, loadingNext, loadingPrev, contentTitle, alternativeLabel, breakpoint, orientation, activeStep, ...otherProps } = props;
    return (_jsx(MuiStep, { ...otherProps, children: _jsxs(StepHorizontalLabelStyled, { StepIconComponent: StepIconHorizontal, StepIconProps: { checkmark: alternativeLabel }, onClick: onClickTitle, clickable: !!onClickTitle, children: [!contentTitle && _jsx(Typography, { color: "inherit", children: label }), _jsx(Typography, { color: "inherit", variant: "caption", children: subtitle })] }) }));
}
const StepHorizontalLabelStyled = styled(MuiStepLabel, {
    shouldForwardProp: (prop) => prop !== 'clickable',
})(({ clickable }) => ({
    '& > span': {
        paddingRight: 0,
    },
    ...(clickable && {
        cursor: 'pointer !important',
    }),
}));
// StepVertical
function StepVertical(props) {
    const { first, last, disabledNext, disabledPrev, onClickPrev, onClickNext, onClickTitle, children, label, subtitle, labelNext, labelPrev, scrollOffset, forcedStep, setAnimating, loadingNext, loadingPrev, loading, activeStep, alternativeLabel, contentTitle, ...otherProps } = props;
    const propsWithoutSetAnimating = dissoc('setAnimating', props);
    const labelRef = useRef(null);
    const onEntered = useCallback(() => {
        const element = labelRef.current;
        if (!element) {
            return;
        }
        if (setAnimating)
            setAnimating(false);
        if (!forcedStep) {
            const { top } = element.getBoundingClientRect();
            const { top: bodyTop } = document.body.getBoundingClientRect();
            const offsetPosition = top - bodyTop - (scrollOffset || 0);
            window.scrollTo({
                behavior: 'smooth',
                top: offsetPosition,
            });
        }
    }, [scrollOffset, forcedStep, setAnimating]);
    // FIXME: this triggers too late (when the animation had already started)
    //        and there does not seem to be a handler that sets it early enough,
    //        so we now set it to `true` in the useScrollTo hook
    // const onEnter = React.useCallback(() => {
    //   setAnimating(true);
    // }, [setAnimating]);
    const TransitionProps = useMemo(() => ({
        timeout: 200,
        onEntered,
    }), [onEntered]);
    return (_jsxs(MuiStep, { ...otherProps, children: [_jsx(StepVerticalLabelStyled, { StepIconComponent: StepIconVertical, onClick: onClickTitle, active: otherProps.active, clickable: !!onClickTitle, ref: labelRef, children: _jsx(StepVerticalTypographyStyled, { active: otherProps.active, variant: otherProps.active ? 'h4' : 'subtitle1', children: label }) }), _jsx(StepVerticalContentStyled, { TransitionProps: TransitionProps, active: otherProps.active, children: _jsxs("div", { children: [loading ? _jsx(StepContentSkeleton, {}) : children, _jsx(StepNavigation, { loading: loading, ...propsWithoutSetAnimating, loadingNext: loadingNext, loadingPrev: loadingPrev })] }) })] }));
}
const StepVerticalLabelStyled = styled(MuiStepLabel, {
    shouldForwardProp: (prop) => prop !== 'active' && prop !== 'clickable',
})(({ theme, active, clickable }) => ({
    '& 	.MuiStepLabel-iconContainer': {
        paddingRight: `${theme.spacing(3)} !important`,
        [theme.breakpoints.up('lg')]: {
            paddingRight: `${theme.spacing(4.5)} !important`,
        },
    },
    ...(active && {
        marginBottom: '26px',
    }),
    ...(clickable && {
        cursor: 'pointer !important',
    }),
}));
const StepVerticalTypographyStyled = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
    ...(active && {
        color: theme.palette.secondary.main,
    }),
}));
const StepVerticalContentStyled = styled(MuiStepContent, {
    shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
    borderLeft: `1px solid ${theme.palette.secondary.dark}`,
    paddingLeft: `${theme.spacing(4.5)} !important`,
    [theme.breakpoints.up('lg')]: {
        paddingLeft: `${theme.spacing(6)} !important`,
    },
    ...(active && {
        marginBottom: '8px',
    }),
}));
const StepContentSkeletonTitleStyled = styled(Skeleton)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    width: '50%',
    height: 30,
    borderRadius: 4,
}));
const StepContentSkeletonContentStyled = styled(Skeleton)(({ theme }) => ({
    marginBottom: theme.spacing(4),
    width: '80%',
    height: 80,
    borderRadius: 4,
}));
export function StepContentSkeleton() {
    return (_jsx(_Fragment, { children: [1, 2, 3, 4].map((_, index) => (_jsxs(Fragment, { children: [_jsx(StepContentSkeletonTitleStyled, { animation: "wave", variant: "text" }), _jsx(StepContentSkeletonContentStyled, { animation: "wave", variant: "rectangular" })] }, index))) }));
}
